import React, { useState } from 'react';
import '../styles/PaymentForm.css';

// Types
interface ProductTier {
  id: string;
  name: string;
  description: string;
  price_id: string;
  amount: number;
  currency: string;
  interval: string;
  features: string[];
  metadata: Record<string, string>;
}

interface PaymentFormProps {
  tier: ProductTier;
  onSuccess: () => void;
  onCancel: () => void;
  onError: (error: string) => void;
}

const PaymentForm: React.FC<PaymentFormProps> = ({ tier, onSuccess, onCancel, onError }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Create payment intent for the tier
      const response = await fetch('/api/create-payment-intent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          price_id: tier.price_id,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to create payment intent');
      }

      // For now, simulate a successful payment
      // In a real implementation, this would integrate with Stripe Elements
      // or redirect to Stripe Checkout
      
      // Simulate payment processing delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Simulate successful payment confirmation
      const confirmResponse = await fetch('/api/confirm-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          payment_intent_id: 'pi_simulated_success',
        }),
      });

      const confirmData = await confirmResponse.json();
      
      if (confirmData.success) {
        onSuccess();
      } else {
        throw new Error(confirmData.error || 'Failed to confirm subscription');
      }
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Payment failed';
      setError(errorMessage);
      onError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const formatPrice = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  return (
    <div className="payment-form-modal">
      <div className="payment-form-content">
        <div className="payment-form-header">
          <h3>Complete Your Subscription</h3>
          <button 
            type="button" 
            className="btn-close" 
            aria-label="Close"
            onClick={onCancel}
          />
        </div>

        <div className="plan-summary">
          <h4>{tier.name}</h4>
          <p className="plan-price">
            {formatPrice(tier.amount, tier.currency)}/{tier.interval}
          </p>
          <ul className="plan-features">
            {tier.features.map((feature, index) => (
              <li key={index}>{feature}</li>
            ))}
          </ul>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="payment-info">
            <div className="alert alert-info">
              <strong>Milestone 4 Implementation:</strong> This simulates the payment flow. 
              In the complete implementation, this would integrate with Stripe Elements 
              for secure card input and payment processing.
            </div>
          </div>

          <div className="payment-actions">
            <button 
              type="button" 
              className="btn btn-secondary me-2" 
              onClick={onCancel}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary" 
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Processing Payment...
                </>
              ) : (
                `Subscribe for ${formatPrice(tier.amount, tier.currency)}`
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentForm;
